import {
  Modal,
  Avatar,
  Typography,
  Divider,
  Tag,
  Space,
  Row,
  Col,
  Skeleton,
} from "antd";
import { BulbOutlined } from "@ant-design/icons"; // For experience item bullets
import { filterProfilePicture } from "../../helper";
import "./TeacherDetailPopup.css";
import ButtonComponent from "../button";
import TeacherDetailSkeleton from "./skeleton"; // adjust path as needed
import useUserStatus from "../../helper/use-user-status";

const { Title, Text } = Typography;

const TeacherDetailPopup = ({ teacherDetail, onClose, visible, isLoading }) => {
  const activeStatus = useUserStatus(teacherDetail?.uuid);

  const avatarSrc =
    filterProfilePicture(teacherDetail?.teacherMedia) ||
    require("../../assets/images/avatar.jpg");

  const handleViewProfile = () => {
    window.open(
      `/teacherProfile/${teacherDetail?.uuid}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const renderExperienceList = (items) => (
    <ul className="experience-list">
      {items.map((item, index) => (
        <li key={index}>
          <div className="experience-item">
            <BulbOutlined className="experience-icon" />
            <div>
              <Text strong>{item.position}</Text>
              <br />
              {item?.position && (
                <Text type="secondary"> - {item?.organization}</Text>
              )}
              {item?.duration && (
                <Text type="secondary" className="duration">
                  {item.duration}
                </Text>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        !isLoading && (
          <div className="modal-footer">
            <ButtonComponent type="primary" onClick={handleViewProfile}>
              View Profile
            </ButtonComponent>
          </div>
        )
      }
      centered
      width={550}
      style={{
        top: 20,
      }}
      className="teacher-detail-modal"
      title={
        !teacherDetail && isLoading ? (
          <Skeleton avatar paragraph={{ rows: 1 }} active />
        ) : (
          <div className="teacher-header">
            <div className="teacher-avatar-container">
              <Avatar src={avatarSrc} size={70} className="teacher-avatar" />
              {/* Online status indicator */}
              <div
                className={`teacher-online-status ${
                  activeStatus === "online" ? "online" : "offline"
                }`}
              ></div>
            </div>
            <div className="teacher-info">
              <Title level={4} className="teacher-name">
                {teacherDetail?.fullName || "Unnamed"}
              </Title>
              <Text type="secondary" className="teacher-intro">
                {teacherDetail?.intro
                  ? teacherDetail.intro.length > 100
                    ? teacherDetail.intro.slice(0, 100) + "..."
                    : teacherDetail.intro
                  : "Experienced educator"}
              </Text>
            </div>
          </div>
        )
      }
    >
      {isLoading ? (
        <TeacherDetailSkeleton />
      ) : (
        <div className="teacher-details-container">
          {/* Academic Experience */}
          <div className="detail-section">
            <Row gutter={0}>
              <Col span={6}>
                <Title level={5} className="section-title">
                  Academic Experience
                </Title>
              </Col>
              <Col span={18}>
                {teacherDetail?.teacherAcademicExperiences?.length > 0 ? (
                  renderExperienceList(teacherDetail.teacherAcademicExperiences)
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Col>
            </Row>
          </div>
          <Divider className="section-divider" />

          {/* Corporate Experience */}
          <div className="detail-section">
            <Row gutter={16}>
              <Col span={6}>
                <Title level={5} className="section-title">
                  Corporate Experience
                </Title>
              </Col>
              <Col span={18}>
                {teacherDetail?.teacherCorporateExperiences?.length > 0 ? (
                  renderExperienceList(
                    teacherDetail.teacherCorporateExperiences
                  )
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Col>
            </Row>
          </div>
          <Divider className="section-divider" />

          {/* Courses */}
          <div className="detail-section">
            <Row gutter={16}>
              <Col span={6}>
                <Title level={5} className="section-title">
                  Courses
                </Title>
              </Col>
              <Col span={18}>
                {teacherDetail?.courses?.length > 0 ? (
                  <Space wrap>
                    {teacherDetail.courses.map((item, index) => (
                      <Tag key={index} className="course-tag">
                        {item?.courseSubjectName}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Col>
            </Row>
          </div>
          <Divider className="section-divider" />

          {/* Boards */}
          <div className="detail-section">
            <Row gutter={16}>
              <Col span={6}>
                <Title level={5} className="section-title">
                  Boards
                </Title>
              </Col>
              <Col span={18}>
                {teacherDetail?.teacherBoards?.length > 0 ? (
                  <ul className="detail-list">
                    {teacherDetail.teacherBoards.map((item, i) => (
                      <li key={i}>{item?.boardName}</li>
                    ))}
                  </ul>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TeacherDetailPopup;
